import { useState } from "react";
import type { Visibility } from "@msf/shared";
import { CheckIcon, GlobeIcon, HourglassIcon, LockIcon } from "./icons.js";

export function WaitingOverlay({
  joinCode,
  visibility,
  isHost,
}: {
  joinCode: string;
  visibility: Visibility;
  isHost: boolean;
}) {
  const [copied, setCopied] = useState(false);
  const shareUrl = `${window.location.origin}/j/${joinCode}`;

  async function share() {
    const shareData = {
      title: "Minesweeper Flags",
      text: "Come play Minesweeper Flags with me!",
      url: shareUrl,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
        return;
      }
    } catch {
      /* user cancelled — fall through to copy */
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  }

  return (
    <div className="fixed inset-0 z-40 grid place-items-center bg-black/40 p-4 backdrop-blur-sm">
      <div className="clay w-full max-w-sm p-6 text-center">
        <div className="hourglass-wrap mx-auto mb-3 h-16 w-16">
          <div className="hourglass-flip h-full w-full">
            <HourglassIcon className="h-full w-full" />
          </div>
          {/* Sand always falls straight down in screen space — kept outside the
              rotating element above so gravity never visually reverses mid-flip. */}
          <span className="sand-grain" style={{ animationDelay: "0s" }} />
          <span className="sand-grain" style={{ animationDelay: "0.38s" }} />
          <span className="sand-grain" style={{ animationDelay: "0.76s" }} />
        </div>
        <h2 className="text-xl font-black">{isHost ? "Waiting for an opponent" : "Getting you seated…"}</h2>
        <p className="mt-1 text-sm opacity-60">
          {isHost ? "Share this game so a friend can join." : "The host will start once you're in."}
        </p>

        <div className="clay-inset mt-4 flex items-center justify-between px-4 py-3">
          <div className="text-left">
            <p className="text-[0.6rem] uppercase tracking-wide opacity-50">Game code</p>
            <p className="text-2xl font-black tracking-[0.2em]" data-testid="join-code">
              {joinCode}
            </p>
          </div>
          {visibility === "private" ? (
            <LockIcon className="h-7 w-7" alt="Private game" />
          ) : (
            <GlobeIcon className="h-7 w-7" alt="Public game" />
          )}
        </div>

        {isHost && (
          <button
            type="button"
            onClick={share}
            className="clay-btn clay-accent mt-4 flex w-full items-center justify-center gap-1.5 py-3 font-extrabold"
          >
            {copied && <CheckIcon className="h-4 w-4" />}
            {copied ? "Link copied!" : "Share invite link"}
          </button>
        )}
        {visibility === "private" && isHost && (
          <p className="mt-3 flex items-center justify-center gap-1 text-xs opacity-60">
            <LockIcon className="h-4 w-4" alt="" /> Don't forget to tell them the PIN
          </p>
        )}
      </div>
    </div>
  );
}
